import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaSpinner,
  FaCalendarAlt,
  FaPrint,
  FaDownload,
  FaUser,
  FaWifi,
  FaBan
} from 'react-icons/fa';
import './OutageEnquiryPanel.scss';

/** Day name abbreviations for date formatting */
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Month name abbreviations for date formatting */
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Parses a date string (yyyy-MM-dd) into a local Date object without timezone shifting.
 * Appending 'T00:00:00' ensures the date is interpreted in local time rather than UTC.
 */
const parseLocalDate = (dateStr) => new Date(dateStr + 'T00:00:00');

/**
 * Formats a local Date object to YYYY-MM-DD string using local date parts.
 * Avoids toISOString() which converts to UTC and can shift the day in non-UTC timezones.
 */
const toLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Formats a date string (yyyy-MM-dd) into a more readable format (e.g., "Mon 5 May").
 */
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  const date = parseLocalDate(dateStr);
  return `${DAY_NAMES[date.getDay()]} ${date.getDate()} ${MONTH_NAMES[date.getMonth()]}`;
};

/**
 * OutageEnquiryPanel — Intelligence sub-tab component for Router Detail Page.
 * Correlates router outage data (InfluxDB) with CM Daily Checklist submission gaps
 * to confirm/deny whether an employee was unable to complete their checklist
 * due to an internet outage at this router's location.
 *
 * Features:
 * - Date range selection with quick presets (Last 7/14/30 days, This Month)
 * - User dropdown with searchable list from /api/User/all
 * - Visual heatmap calendar showing daily availability and missed checklists
 * - Summary statistics with conclusion
 * - Correlation analysis table
 * - Export/Print functionality for HR reporting
 *
 * @param {number} routerId - The router configuration ID
 * @param {function} apiFetch - Authenticated fetch function from useAuth
 * @param {number|null} propertyId - The property associated with this router
 */
const OutageEnquiryPanel = ({ routerId, apiFetch, propertyId }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [setHoveredDay] = useState(null);
  const [activePreset, setActivePreset] = useState(null);

  /**
   * Loads all active users on mount for the user filter dropdown.
   * Uses /api/User/all which returns { id, name, email } format.
   */
  useEffect(() => {
    const loadUsers = async () => {
      setUsersLoading(true);
      try {
        const response = await apiFetch('/api/User/all');
        if (response.ok) {
          const data = await response.json();
          const userList = data.data || data || [];
          setUsers(Array.isArray(userList) ? userList : []);
        }
      } catch (err) {
        console.debug('Failed to load users for outage enquiry:', err);
      } finally {
        setUsersLoading(false);
      }
    };
    loadUsers();
  }, [apiFetch]);

  /** O(1) user lookup map keyed by user ID for efficient name resolution */
  const userMap = useMemo(() => {
    const map = new Map();
    users.forEach(u => map.set(u.id, u));
    return map;
  }, [users]);

  /**
   * Applies a date preset by calculating start/end dates relative to today.
   */
  const applyPreset = useCallback((preset) => {
    const today = new Date();
    let newStart;
    let newEnd = toLocalDateString(today);

    switch (preset) {
      case '7d':
        newStart = new Date(today);
        newStart.setDate(today.getDate() - 7);
        break;
      case '14d':
        newStart = new Date(today);
        newStart.setDate(today.getDate() - 14);
        break;
      case '30d':
        newStart = new Date(today);
        newStart.setDate(today.getDate() - 30);
        break;
      case 'thisMonth':
        newStart = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'lastMonth': {
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        newStart = lastMonth;
        newEnd = toLocalDateString(new Date(today.getFullYear(), today.getMonth(), 0));
        break;
      }
      default:
        return;
    }

    setStartDate(toLocalDateString(newStart));
    setEndDate(newEnd);
    setActivePreset(preset);
  }, []);

  /**
   * Performs the outage enquiry by calling the backend correlation endpoint.
   * Validates inputs before making the API call.
   */
  const handleEnquiry = useCallback(async () => {
    if (!startDate || !endDate) {
      setError('Please select both a start and end date.');
      return;
    }

    if (new Date(endDate) < new Date(startDate)) {
      setError('End date must be on or after start date.');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const params = new URLSearchParams({ startDate, endDate });

      if (selectedUserId) {
        params.append('userId', selectedUserId);
      }

      const response = await apiFetch(`/api/RouterConfig/${routerId}/outage-enquiry?${params.toString()}`);
      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'Failed to retrieve outage enquiry data.');
        return;
      }

      setResult(data);
    } catch (err) {
      setError('An unexpected error occurred while processing the enquiry.');
      console.error('Outage enquiry error:', err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedUserId, routerId, apiFetch]);

  /**
   * Builds a heatmap data structure from the daily availability and missed dates.
   */
  const heatmapData = useMemo(() => {
    if (!result) return [];

    const availabilityMap = {};
    (result.dailyAvailability || []).forEach(day => {
      availabilityMap[day.date] = day;
    });

    const missedSet = new Set(result.missedDates || []);
    const submittedSet = new Set(result.submittedDates || []);
    const days = [];

    if (result.summary?.periodStart && result.summary?.periodEnd) {
      const start = parseLocalDate(result.summary.periodStart);
      const end = parseLocalDate(result.summary.periodEnd);

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dateStr = toLocalDateString(d);
        const dayOfWeek = d.getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        const availability = availabilityMap[dateStr];

        days.push({
          date: dateStr,
          dayLabel: d.getDate().toString(),
          isWeekend,
          isMissed: missedSet.has(dateStr),
          isSubmitted: submittedSet.has(dateStr),
          uptimePercentage: availability?.uptimePercentage ?? null,
          hadOutage: availability?.hadOutage ?? false,
          fullOutage: availability?.fullOutage ?? false
        });
      }
    }

    return days;
  }, [result]);

  /**
   * Determines the CSS class for a heatmap day cell based on its data.
   */
  const getDayClass = (day) => {
    let classes = 'oep-heatmap-day';
    if (day.isWeekend) return classes + ' oep-day-weekend';
    if (day.fullOutage) classes += ' oep-day-full-outage';
    else if (day.hadOutage) classes += ' oep-day-partial';
    else if (day.uptimePercentage !== null) classes += ' oep-day-online';
    else classes += ' oep-day-no-data';
    if (day.isMissed) classes += ' oep-day-missed';
    return classes;
  };

  /**
   * Gets the conclusion CSS class based on the result summary.
   */
  const getConclusionClass = () => {
    if (!result?.summary) return 'oep-conclusion oep-conclusion-neutral';
    if (result.summary.missedWithoutOutage === 0 && result.summary.totalMissed > 0) {
      return 'oep-conclusion oep-conclusion-positive';
    }
    if (result.summary.totalMissed === 0) {
      return 'oep-conclusion oep-conclusion-neutral';
    }
    return 'oep-conclusion oep-conclusion-mixed';
  };

  /**
   * Handles print/export of the enquiry results for reporting purposes.
   */
  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  /**
   * Exports the enquiry results as a CSV file for record keeping.
   */
  const handleExportCsv = useCallback(() => {
    if (!result) return;

    const rows = [
      ['Outage Enquiry Report'],
      [`Router: ${result.summary.routerName} (${result.summary.routerIp})`],
      [`Period: ${result.summary.periodStart} to ${result.summary.periodEnd}`],
      [`Selected User: ${selectedUserId ? userMap.get(parseInt(selectedUserId))?.name || selectedUserId : 'All Users'}`],
      [],
      ['Conclusion:', result.summary.conclusion],
      [],
      ['Date', 'Uptime %', 'Had Outage', 'Checklist Submitted', 'Checklist Missed', 'Verdict']
    ];

    // Add daily data
    (result.dailyAvailability || []).forEach(day => {
      const isMissed = (result.missedDates || []).includes(day.date);
      const isSubmitted = (result.submittedDates || []).includes(day.date);
      const correlation = (result.correlations || []).find(c => c.date === day.date);

      rows.push([
        day.date,
        day.uptimePercentage !== null ? `${day.uptimePercentage}%` : 'No data',
        day.hadOutage ? 'Yes' : 'No',
        isSubmitted ? 'Yes' : 'No',
        isMissed ? 'Yes' : 'No',
        correlation?.verdict || '-'
      ]);
    });

    const csvContent = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `outage-enquiry-${result.summary.routerName}-${result.summary.periodStart}-to-${result.summary.periodEnd}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }, [result, selectedUserId, userMap]);

  /**
   * Renders the verdict icon based on the correlation result.
   */
  const getVerdictIcon = (routerHadOutage, checklistSubmitted) => {
    if (checklistSubmitted && routerHadOutage) {
      return <FaExclamationTriangle className="oep-verdict-icon warning" />;
    }
    if (routerHadOutage) {
      return <FaCheckCircle className="oep-verdict-icon success" />;
    }
    return <FaTimesCircle className="oep-verdict-icon danger" />;
  };

  return (
    <div className="outage-enquiry-panel">
      {/* ─── Info Banner ─── */}
      <div className="oep-info-banner">
        <FaInfoCircle className="oep-info-icon" />
        <div className="oep-info-content">
          <strong>Outage Enquiry Tool</strong>
          <p>
            Correlates router monitoring data with CM Daily Checklist submissions to verify
            whether an employee was unable to complete their daily checklist due to an internet
            outage. Select a date range and optionally filter by employee to generate a report.
          </p>
        </div>
      </div>

      {/* ─── Query Form ─── */}
      <div className="oep-query-form">
        <div className="oep-form-field">
          <label>
            <FaCalendarAlt />
            Start Date
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setActivePreset(null); }}
            max={endDate || undefined}
          />
        </div>

        <div className="oep-form-field">
          <label>
            <FaCalendarAlt />
            End Date
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setActivePreset(null); }}
            min={startDate || undefined}
            max={toLocalDateString(new Date())}
          />
        </div>

        <div className="oep-form-field">
          <label>
            <FaUser />
            Employee (optional)
          </label>
          <select
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            disabled={usersLoading}
          >
            <option value="">All employees</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>
                {user.name} ({user.email})
              </option>
            ))}
          </select>
        </div>

        <button
          className="oep-submit-btn"
          onClick={handleEnquiry}
          disabled={loading || !startDate || !endDate}
        >
          {loading ? <FaSpinner className="oep-spin" /> : <FaSearch />}
          {loading ? 'Analysing...' : 'Run Enquiry'}
        </button>

        {/* Date presets */}
        <div className="oep-date-presets">
          {[
            { key: '7d', label: 'Last 7 Days' },
            { key: '14d', label: 'Last 14 Days' },
            { key: '30d', label: 'Last 30 Days' },
            { key: 'thisMonth', label: 'This Month' },
            { key: 'lastMonth', label: 'Last Month' }
          ].map(preset => (
            <button
              key={preset.key}
              className={`oep-preset-btn ${activePreset === preset.key ? 'active' : ''}`}
              onClick={() => applyPreset(preset.key)}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* ─── Error Alert ─── */}
      {error && (
        <div className="oep-alert oep-alert-error">
          <FaTimesCircle />
          {error}
        </div>
      )}

      {/* ─── No Property Warning ─── */}
      {!propertyId && (
        <div className="oep-alert oep-alert-warning">
          <FaExclamationTriangle />
          This router is not linked to a property. Checklist correlation will not be available — only router availability data will be shown.
        </div>
      )}

      {/* ─── Results ─── */}
      {result && (
        <div className="oep-results">
          {/* Export Bar */}
          <div className="oep-export-bar">
            <button onClick={handleExportCsv} title="Export as CSV">
              <FaDownload /> Export CSV
            </button>
            <button onClick={handlePrint} title="Print report">
              <FaPrint /> Print
            </button>
          </div>

          {/* ─── Summary Card ─── */}
          <div className="oep-card">
            <div className="oep-card-header">
              <h3>
                <FaInfoCircle style={{ color: '#3b82f6' }} />
                Enquiry Summary
              </h3>
            </div>

            <div className="oep-stats-grid">
              <StatCard label="Period" value={`${result.summary.periodStart} → ${result.summary.periodEnd}`} />
              <StatCard label="Weekdays" value={result.summary.totalWeekdays} />
              <StatCard label="Submitted" value={result.summary.totalSubmissions} color="#10b981" />
              <StatCard label="Missed" value={result.summary.totalMissed} color="#ef4444" />
              <StatCard label="Outage Days" value={result.summary.totalOutageDays} color="#f59e0b" />
              <StatCard label="Missed (Outage)" value={result.summary.missedWithOutage} color="#10b981" />
              <StatCard label="Missed (No Outage)" value={result.summary.missedWithoutOutage} color="#ef4444" />
            </div>

            {/* Conclusion */}
            <div className={getConclusionClass()}>
              <strong>Conclusion:</strong>
              <p>{result.summary.conclusion}</p>
            </div>
          </div>

          {/* ─── Availability Heatmap ─── */}
          {heatmapData.length > 0 && (
            <div className="oep-card">
              <div className="oep-card-header">
                <h3>
                  <FaCalendarAlt style={{ color: '#6366f1' }} />
                  Availability Calendar
                </h3>
              </div>

              <div className="oep-heatmap">
                {heatmapData.map((day, idx) => (
                  <div
                    key={idx}
                    className={getDayClass(day)}
                    onMouseEnter={() => setHoveredDay(day)}
                    onMouseLeave={() => setHoveredDay(null)}
                    title={`${day.date} — ${day.isWeekend ? 'Weekend' : day.fullOutage ? 'Full Outage' : day.hadOutage ? `Partial (${day.uptimePercentage}%)` : day.uptimePercentage !== null ? `Online (${day.uptimePercentage}%)` : 'No data'}${day.isMissed ? ' — MISSED' : ''}`}
                  >
                    {day.dayLabel}
                  </div>
                ))}

                {/* Legend */}
                <div className="oep-heatmap-legend">
                  <div className="oep-legend-item">
                    <div className="oep-legend-swatch" style={{ background: '#d1fae5' }} />
                    Online (≥90%)
                  </div>
                  <div className="oep-legend-item">
                    <div className="oep-legend-swatch" style={{ background: '#fef3c7' }} />
                    Partial Outage
                  </div>
                  <div className="oep-legend-item">
                    <div className="oep-legend-swatch" style={{ background: '#fecaca' }} />
                    Full Outage
                  </div>
                  <div className="oep-legend-item">
                    <div className="oep-legend-swatch" style={{ background: '#f1f5f9' }} />
                    No Data
                  </div>
                  <div className="oep-legend-item">
                    <div className="oep-legend-swatch" style={{ background: 'transparent', border: '2px solid #ef4444' }} />
                    Missed Checklist
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ─── Missed Checklist Correlation Table ─── */}
          {result.correlations && result.correlations.length > 0 && (
            <div className="oep-card">
              <div className="oep-card-header">
                <h3>
                  <FaExclamationTriangle style={{ color: '#f59e0b' }} />
                  Missed Checklist Analysis
                </h3>
              </div>

              <div className="oep-table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Router Status</th>
                      <th>Excuse</th>
                      <th>Verdict</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.correlations.map((corr, idx) => (
                      <tr key={idx}>
                        <td>{formatDateDisplay(corr.date)}</td>
                        <td>
                          <span className={`oep-status-badge ${corr.routerHadOutage ? 'oep-badge-outage' : 'oep-badge-online'}`}>
                            {getVerdictIcon(corr.routerHadOutage, corr.checklistSubmitted)}
                            {corr.routerHadOutage ? 'Outage Detected' : 'Online'}
                          </span>
                        </td>
                        <td>
                          {corr.excuse
                            ? <span className="oep-excuse-text">{corr.excuse}</span>
                            : <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>None provided</span>
                          }
                        </td>
                        <td>{corr.verdict}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ─── Submitted Despite Outage ─── */}
          {result.submittedDuringOutage && result.submittedDuringOutage.length > 0 && (
            <div className="oep-card">
              <div className="oep-card-header">
                <h3>
                  <FaCheckCircle style={{ color: '#10b981' }} />
                  Submitted Despite Outage
                </h3>
              </div>
              <p style={{ margin: '0 0 12px', fontSize: '0.85rem', color: '#64748b' }}>
                These dates had router outages but the checklist was still submitted (partial connectivity or alternative access).
              </p>
              <div className="oep-chip-list">
                {result.submittedDuringOutage.map((item, idx) => (
                  <span key={idx} className="oep-chip">
                    {formatDateDisplay(item.date)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ─── Daily Availability Table ─── */}
          {result.dailyAvailability && result.dailyAvailability.length > 0 && (
            <div className="oep-card">
              <div className="oep-card-header">
                <h3>
                  <FaWifi style={{ color: '#06b6d4' }} />
                  Daily Router Availability
                </h3>
              </div>

              <div className="oep-table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Uptime</th>
                      <th>Status</th>
                      <th>Checklist</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.dailyAvailability.map((day, idx) => {
                      const isMissed = (result.missedDates || []).includes(day.date);
                      const isSubmitted = (result.submittedDates || []).includes(day.date);
                      const dateObj = parseLocalDate(day.date);
                      const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;

                      return (
                        <tr key={idx} style={isWeekend ? { opacity: 0.5 } : undefined}>
                          <td>{formatDateDisplay(day.date)}</td>
                          <td>
                            <UptimeBar percentage={day.uptimePercentage} />
                          </td>
                          <td>
                            {day.fullOutage
                              ? <span className="oep-status-badge oep-badge-outage"><FaBan /> Full Outage</span>
                              : day.hadOutage
                                ? <span className="oep-status-badge oep-badge-partial"><FaExclamationTriangle /> Partial</span>
                                : <span className="oep-status-badge oep-badge-online"><FaWifi /> Online</span>}
                          </td>
                          <td>
                            {isWeekend
                              ? <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Weekend</span>
                              : isMissed
                                ? <span style={{ color: '#ef4444', fontWeight: '600', fontSize: '0.8rem' }}>✗ Missed</span>
                                : isSubmitted
                                  ? <span style={{ color: '#10b981', fontWeight: '600', fontSize: '0.8rem' }}>✓ Submitted</span>
                                  : <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * StatCard — Compact metric display card for the summary section.
 * Renders a label and a highlighted value with optional color.
 */
const StatCard = ({ label, value, color }) => (
  <div className="oep-stat-card">
    <div className="oep-stat-label">{label}</div>
    <div className="oep-stat-value" style={color ? { color } : undefined}>{value}</div>
  </div>
);

/**
 * UptimeBar — Visual percentage bar showing router uptime.
 * Color transitions from green (high) to red (low).
 */
const UptimeBar = ({ percentage }) => {
  if (percentage === null || percentage === undefined) {
    return <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>No data</span>;
  }

  const getBarColor = (pct) => {
    if (pct >= 99) return '#10b981';
    if (pct >= 90) return '#f59e0b';
    if (pct >= 50) return '#f97316';
    return '#ef4444';
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div style={{
        flex: 1,
        height: '6px',
        background: '#e2e8f0',
        borderRadius: '3px',
        overflow: 'hidden',
        maxWidth: '80px'
      }}>
        <div style={{
          width: `${percentage}%`,
          height: '100%',
          background: getBarColor(percentage),
          borderRadius: '3px',
          transition: 'width 0.3s ease'
        }} />
      </div>
      <span style={{
        fontSize: '0.82rem',
        fontWeight: '500',
        color: getBarColor(percentage),
        minWidth: '42px'
      }}>
        {percentage}%
      </span>
    </div>
  );
};

export default OutageEnquiryPanel;
